import WebSocketAsPromised from 'websocket-as-promised';
import logger from 'src/utils/logger';
import { HandledError } from 'src/apps/cloud/services/wsErrors';
export class CloudWSClient {
    socketInstance = null;
    connectInFlight = null;
    connectionGeneration = 0;
    reconnectEnabled = true;
    reconnectAttempts = 0;
    reconnectTimer = null;
    reconnectMaxAttempts;
    reconnectBaseDelayMs;
    reconnectMaxDelayMs;
    url;
    t;
    openListeners = [];
    closeListeners = [];
    exhaustedListeners = [];
    reconnectAttemptListeners = [];
    constructor(options) {
        this.url = options.url;
        this.reconnectMaxAttempts = options.reconnectMaxAttempts ?? 10;
        this.reconnectBaseDelayMs = options.reconnectBaseDelayMs ?? 1000;
        this.reconnectMaxDelayMs = options.reconnectMaxDelayMs ?? 30000;
        this.t = options.translate ?? ((key) => key);
    }
    get socket() {
        return this.socketInstance;
    }
    onOpen(listener) {
        this.openListeners.push(listener);
        return () => {
            this.openListeners = this.openListeners.filter((l) => l !== listener);
        };
    }
    onClose(listener) {
        this.closeListeners.push(listener);
        return () => {
            this.closeListeners = this.closeListeners.filter((l) => l !== listener);
        };
    }
    onReconnectExhausted(listener) {
        this.exhaustedListeners.push(listener);
        return () => {
            this.exhaustedListeners = this.exhaustedListeners.filter((l) => l !== listener);
        };
    }
    onReconnectAttempt(listener) {
        this.reconnectAttemptListeners.push(listener);
        return () => {
            this.reconnectAttemptListeners = this.reconnectAttemptListeners.filter((l) => l !== listener);
        };
    }
    async connect() {
        const ws = this.socketInstance;
        if (ws?.isOpened || ws?.isOpening)
            return true;
        this.reconnectEnabled = true;
        if (this.connectInFlight)
            return await this.connectInFlight;
        this.connectInFlight = this.openNewSocket().finally(() => {
            this.connectInFlight = null;
        });
        return await this.connectInFlight;
    }
    disconnect() {
        this.reconnectEnabled = false;
        this.clearReconnectTimer();
        this.connectionGeneration++;
        const ws = this.socketInstance;
        this.socketInstance = null;
        if (!ws)
            return;
        try {
            ws.removeAllListeners?.();
        }
        catch (e) {
            logger.debug('WS client disconnect: removeAllListeners failed', e);
        }
        try {
            void ws.close?.();
        }
        catch (e) {
            logger.debug('WS client disconnect: close failed', e);
        }
    }
    clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
    emitOpen(ws) {
        for (const listener of this.openListeners)
            listener(ws);
    }
    emitClose(event) {
        for (const listener of this.closeListeners)
            listener(event);
    }
    emitExhausted() {
        for (const listener of this.exhaustedListeners)
            listener();
    }
    emitReconnectAttempt(info) {
        for (const listener of this.reconnectAttemptListeners)
            listener(info);
    }
    scheduleReconnect() {
        if (!this.reconnectEnabled)
            return;
        if (this.reconnectAttempts >= this.reconnectMaxAttempts) {
            logger.warn('WS client: reconnection attempts exhausted');
            this.emitExhausted();
            return;
        }
        const attempt = ++this.reconnectAttempts;
        const delay = Math.min(this.reconnectBaseDelayMs * Math.pow(2, attempt - 1), this.reconnectMaxDelayMs);
        logger.warn(`WS client: reconnecting (attempt ${attempt}/${this.reconnectMaxAttempts}) in ${delay}ms...`);
        this.emitReconnectAttempt({ attempt, maxAttempts: this.reconnectMaxAttempts, delayMs: delay });
        this.clearReconnectTimer();
        this.reconnectTimer = setTimeout(async () => {
            // Avoid parallel reconnect attempts (e.g. multiple close events).
            if (this.connectInFlight) {
                const ok = await this.connectInFlight;
                if (!ok)
                    this.scheduleReconnect();
                return;
            }
            this.connectInFlight = this.openNewSocket().finally(() => {
                this.connectInFlight = null;
            });
            const ok = await this.connectInFlight;
            if (!ok)
                this.scheduleReconnect();
        }, delay);
    }
    async openNewSocket() {
        this.clearReconnectTimer();
        const generation = ++this.connectionGeneration;
        // Ensure only one active socket exists: detach and close previous before replacing.
        const previous = this.socketInstance;
        this.socketInstance = null;
        if (previous) {
            try {
                previous.removeAllListeners?.();
            }
            catch (e) {
                logger.debug('WS client: failed to remove listeners from previous socket', e);
            }
            try {
                void previous.close?.();
            }
            catch (e) {
                logger.debug('WS client: failed to close previous socket', e);
            }
        }
        const ws = new WebSocketAsPromised(this.url, {
            packMessage: (data) => JSON.stringify(data),
            unpackMessage: (data) => JSON.parse(data, (key, value) => {
                if (key === '__proto__' || key === 'constructor' || key === 'prototype')
                    return undefined;
                return value;
            }),
            attachRequestId: (data, requestId) => Object.assign({ id: requestId }, data),
            extractRequestId: (data) => data && data.id
        });
        const originalSendRequest = ws.sendRequest.bind(ws);
        ws.sendRequest = async (data, options) => {
            const result = await originalSendRequest(data, options);
            if (result && result.type === 'error') {
                const message = result.args?.message || this.t('error');
                throw new HandledError(message);
            }
            return result;
        };
        ws.onClose.addListener((event) => {
            // Ignore closes from stale sockets (prevents reconnect storms / duplicate sockets).
            if (generation !== this.connectionGeneration)
                return;
            if (this.socketInstance !== ws)
                return;
            this.emitClose(event);
            this.scheduleReconnect();
        });
        try {
            await ws.open();
            // If a newer connection attempt started while we were opening, close this one.
            if (generation !== this.connectionGeneration) {
                try {
                    ws.removeAllListeners?.();
                }
                catch (e) {
                    logger.debug('WS client: failed to cleanup stale socket listeners', e);
                }
                try {
                    void ws.close?.();
                }
                catch (e) {
                    logger.debug('WS client: failed to close stale socket', e);
                }
                return false;
            }
            this.socketInstance = ws;
            this.reconnectAttempts = 0;
            logger.log(`${this.url} connected`);
            this.emitOpen(ws);
            return true;
        }
        catch (e) {
            logger.error('WS client: connection error', e);
            return false;
        }
    }
}
